import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const Advanced: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const modules = [
    {
      title: 'Algorithmic Trading',
      topics: [
        'Introduction to automated trading systems',
        'Building trading algorithms and bots',
        'Backtesting strategies with historical data',
        'Optimization and parameter tuning',
        'Risk controls for automated systems',
        'API integration and execution',
        'Common pitfalls in algo trading'
      ],
      duration: '8-10 hours'
    },
    {
      title: 'Quantitative Analysis',
      topics: [
        'Statistical analysis of market data',
        'Probability and expected value in trading',
        'Monte Carlo simulations',
        'Correlation and regression analysis',
        'Building quantitative models',
        'Machine learning basics for trading',
        'Data mining and pattern discovery'
      ],
      duration: '8-10 hours'
    },
    {
      title: 'Multi-Asset Strategies',
      topics: [
        'Portfolio construction and optimization',
        'Cross-asset correlation trading',
        'Pairs trading and statistical arbitrage',
        'Market-neutral strategies',
        'Sector rotation strategies',
        'Global macro trading approaches',
        'Alternative data sources'
      ],
      duration: '6-8 hours'
    },
    {
      title: 'Performance Optimization',
      topics: [
        'Advanced position sizing models',
        'Kelly Criterion and optimal f',
        'Portfolio heat and exposure management',
        'Dynamic leverage adjustment',
        'Sharpe ratio optimization',
        'Maximum adverse excursion (MAE) analysis',
        'Professional-level performance metrics'
      ],
      duration: '6-8 hours'
    }
  ];

  const advancedTopics = [
    { 
      name: 'Order Flow Analysis', 
      description: 'Read institutional order flow and market microstructure',
      icon: '📊'
    },
    { 
      name: 'Options Strategies', 
      description: 'Advanced options strategies for hedging and income generation',
      icon: '📈'
    },
    { 
      name: 'High-Frequency Trading', 
      description: 'Understanding HFT and ultra-low latency trading',
      icon: '⚡'
    },
    { 
      name: 'Risk Modeling', 
      description: 'Value at Risk (VaR) and stress testing portfolios',
      icon: '🛡️'
    },
    { 
      name: 'Sentiment Analysis', 
      description: 'Using social media and news sentiment in trading',
      icon: '💭'
    },
    { 
      name: 'Execution Algorithms', 
      description: 'TWAP, VWAP, and smart order routing',
      icon: '🎯'
    }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Advanced Trading Course</h1>
          <p>Master professional-level trading and quantitative strategies</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Course Overview</h2>
          <p>
            The Advanced course is designed for experienced traders ready to operate at a professional level. 
            You'll learn algorithmic trading, quantitative analysis, multi-asset portfolio management, and 
            advanced optimization techniques used by hedge funds and proprietary trading firms.
          </p>
          <div className="prerequisites-box">
            <h3>📋 Prerequisites</h3>
            <ul>
              <li>Completed Intermediate course or 2+ years of trading experience</li>
              <li>Demonstrated profitability over at least 6 months</li>
              <li>Strong understanding of technical and fundamental analysis</li>
              <li>Proficiency with programming (Python/MQL preferred) or willingness to learn</li>
              <li>Understanding of statistics and probability</li>
            </ul>
          </div>
          <div className="course-stats">
            <div className="stat">
              <h3>28-36 Hours</h3>
              <p>Intensive Training</p>
            </div>
            <div className="stat">
              <h3>4 Core Modules</h3>
              <p>+ Bonus Content</p>
            </div>
            <div className="stat">
              <h3>1-on-1 Mentoring</h3>
              <p>Personal Guidance</p>
            </div>
            <div className="stat">
              <h3>Pro Certificate</h3>
              <p>Industry Recognized</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Advanced Curriculum</h2>
          <div className="modules-list">
            {modules.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                  <span className="module-duration">⏱️ {module.duration}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.topics.map((topic, topicIndex) => (
                    <li key={topicIndex}>{topic}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="advanced-topics-section" variants={itemVariants}>
          <h2>Specialized Topics Covered</h2>
          <div className="advanced-topics-grid">
            {advancedTopics.map((topic, index) => (
              <motion.div 
                key={index} 
                className="topic-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="topic-icon">{topic.icon}</div>
                <h3>{topic.name}</h3>
                <p>{topic.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="course-benefits" variants={itemVariants}>
          <h2>Professional-Level Training</h2>
          <div className="benefits-grid">
            <div className="benefit">
              <h3>🤖 Build Trading Bots</h3>
              <p>Create automated trading systems that execute your strategies 24/7</p>
            </div>
            <div className="benefit">
              <h3>📊 Quantitative Edge</h3>
              <p>Use statistical and mathematical models to gain market advantage</p>
            </div>
            <div className="benefit">
              <h3>💼 Portfolio Management</h3>
              <p>Manage multi-asset portfolios like institutional traders</p>
            </div>
            <div className="benefit">
              <h3>⚡ Execution Optimization</h3>
              <p>Minimize slippage and maximize fill rates with smart execution</p>
            </div>
            <div className="benefit">
              <h3>🎯 Advanced Risk Models</h3>
              <p>Implement sophisticated risk management and position sizing</p>
            </div>
            <div className="benefit">
              <h3>👨‍💼 Mentorship Program</h3>
              <p>Direct access to professional traders for personalized guidance</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="tools-technologies" variants={itemVariants}>
          <h2>Tools & Technologies You'll Master</h2>
          <div className="tools-grid">
            <div className="tool">
              <h4>Python Programming</h4>
              <p>For data analysis, backtesting, and algorithm development</p>
            </div>
            <div className="tool">
              <h4>MetaTrader MQL</h4>
              <p>Build Expert Advisors and custom indicators</p>
            </div>
            <div className="tool">
              <h4>TradingView Pine Script</h4>
              <p>Create custom strategies and automated alerts</p>
            </div>
            <div className="tool">
              <h4>Data Analysis Libraries</h4>
              <p>Pandas, NumPy, and statistical analysis tools</p>
            </div>
            <div className="tool">
              <h4>Backtesting Frameworks</h4>
              <p>Backtrader, QuantConnect, and custom solutions</p>
            </div>
            <div className="tool">
              <h4>API Integration</h4>
              <p>Connect to broker APIs for automated execution</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="career-paths" variants={itemVariants}>
          <h2>Career Opportunities</h2>
          <p>
            Upon completion of the Advanced course, you'll have the skills required for professional trading roles:
          </p>
          <div className="careers-grid">
            <div className="career">
              <h4>💼 Proprietary Trader</h4>
              <p>Trade with firm capital at prop trading companies</p>
            </div>
            <div className="career">
              <h4>📈 Quantitative Analyst</h4>
              <p>Develop trading models and strategies for hedge funds</p>
            </div>
            <div className="career">
              <h4>🤖 Algo Trading Developer</h4>
              <p>Build and maintain automated trading systems</p>
            </div>
            <div className="career">
              <h4>💰 Independent Trader</h4>
              <p>Trade professionally with your own capital</p>
            </div>
            <div className="career">
              <h4>🎓 Trading Educator</h4>
              <p>Teach others using your advanced knowledge</p>
            </div>
            <div className="career">
              <h4>👔 Portfolio Manager</h4>
              <p>Manage investment portfolios for clients</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="capstone-project" variants={itemVariants}>
          <h2>Capstone Project</h2>
          <div className="project-box">
            <h3>Build Your Complete Trading System</h3>
            <p>
              As your final project, you'll design, build, backtest, and present a complete algorithmic trading system. 
              This includes:
            </p>
            <ul>
              <li>Strategy development and logic documentation</li>
              <li>Historical backtesting with at least 5 years of data</li>
              <li>Risk management and position sizing implementation</li>
              <li>Performance metrics and optimization analysis</li>
              <li>Live testing on demo account for 1 month</li>
              <li>Professional presentation to instructors and peers</li>
            </ul>
            <p className="project-note">
              <strong>Note:</strong> This project demonstrates your ability to think and operate like a professional trader.
            </p>
          </div>
        </motion.section>

        <motion.section className="next-steps" variants={itemVariants}>
          <h2>Your Professional Journey</h2>
          <p>
            Completing the Advanced course marks your transition from retail trader to professional-level operator. 
            You'll have the knowledge, tools, and confidence to pursue trading as a serious career or manage 
            substantial capital effectively.
          </p>
          <div className="progression-path">
            <div className="path-step completed">
              <h4>✓ Beginner</h4>
              <p>Foundation</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step completed">
              <h4>✓ Intermediate</h4>
              <p>Development</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step current">
              <h4>✓ Advanced</h4>
              <p>Professional</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="investment-section" variants={itemVariants}>
          <h2>Course Investment</h2>
          <div className="investment-box">
            <p>
              The Advanced course represents a significant investment in your professional development. 
              The knowledge and skills you'll gain are comparable to what hedge funds and prop firms teach 
              their traders internally.
            </p>
            <div className="investment-benefits">
              <h4>What's Included:</h4>
              <ul>
                <li>28-36 hours of advanced video content</li>
                <li>Live coding sessions and strategy development workshops</li>
                <li>4 one-on-one mentoring sessions with professional traders</li>
                <li>Access to proprietary trading tools and code libraries</li>
                <li>Lifetime access to course materials and updates</li>
                <li>Private community of advanced traders</li>
                <li>Certificate upon successful completion</li>
                <li>Job placement assistance and networking opportunities</li>
              </ul>
            </div>
          </div>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Master Professional Trading</h2>
          <p>Join the elite group of professional-level traders</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Apply for Advanced Course</Link>
            <Link to="/services/signals" className="btn secondary">Professional Signals</Link>
            <Link to="/ib-partners" className="btn secondary">Pro Trading Accounts</Link>
          </div>
          <p className="application-note">
            <em>Note: Due to the intensive nature of this course, enrollment is limited and subject to qualification review.</em>
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Advanced;
